import * as React from 'react';
import {useEffect, useState} from 'react';
import {Box, Chip, Typography, useTheme} from '@mui/material';
import type {TreeItemProps} from '@mui/x-tree-view';
import {TreeItem, useTreeItemModel} from '@mui/x-tree-view';
import {useLocalization} from 'localization';
import {ChevronRightIcon} from 'components/icons/ChevronRightIcon';
import type {ThesaurusTreeViewItem} from '../types';
import {useThesaurusSelectionContext} from '../contexts/ThesaurusSelectionContext';
import {ThesaurusNodeCode} from './ThesaurusNodeCode';

const focusedText = {fontWeight: 'bold'} as const;

export const ThesaurusItem: React.FC<TreeItemProps> = props => {
  const {itemId, ...rest} = props;
  const item = useTreeItemModel<ThesaurusTreeViewItem>(itemId);
  const {t} = useLocalization();
  const {colors} = useTheme();

  const selectedChip = {
    backgroundColor: colors.beige,
    color: colors.warmGreen,
  } as const;

  // Whether node is selected (part of the thesaurus value) or not
  const [selected, setSelected] = useState<boolean>(false);
  // Whether node is focused (clicked by user in the tree) or not
  const [focused, setFocused] = useState<boolean>(false);
  // Whether node has a child that is focused (clicked by user in the tree) or not
  const [childFocused, setChildFocused] = useState<boolean>(false);
  const {subscribe} = useThesaurusSelectionContext();

  useEffect(() => {
    return subscribe(itemId, {
      onSelectedChange: setSelected,
      onFocusedChange: setFocused,
      onChildFocusedChange: setChildFocused,
    });
  }, [itemId, subscribe]);

  return (
    <TreeItem
      {...rest}
      itemId={itemId}
      label={
        <Box className={'labelBox'}>
          <ThesaurusNodeCode code={item?.displayCode} />
          <Typography
            className={'nodeText'}
            variant="body2"
            color="primary"
            style={focused || childFocused ? focusedText : undefined}>
            {item?.label}
          </Typography>
          <Typography className={'nodeStatus'} variant="caption">
            {selected ? (
              <Chip
                label={t('extension.thesaurusEdit.selected')}
                sx={selectedChip}
                color="primary"
                size="small"
              />
            ) : null}
            {item?.onlyTopLevel ? <ChevronRightIcon color={'primary'} /> : null}
          </Typography>
        </Box>
      }
    />
  );
};
